{
  "version": "0.1.38",
  "packageName": "@classic-homes/theme-docs",
  "description": "Markdown documentation components for the Classic theme system",
  "exports": {
    ".": {
      "importPath": "@classic-homes/theme-docs",
      "description": "Main entry point with components, parser utilities, and types"
    },
    "./styles": {
      "importPath": "@classic-homes/theme-docs/styles",
      "description": "Subpath export for styles"
    }
  },
  "components": [
    {
      "name": "MarkdownPage",
      "description": "Full markdown page renderer with frontmatter support, async parsing, and loading/error states",
      "category": "core",
      "props": [
        {
          "name": "content",
          "type": "string",
          "required": true,
          "description": "Raw markdown content (with optional frontmatter)"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Custom class for the content container"
        },
        {
          "name": "theme",
          "type": "'github-dark' | 'github-light' | 'one-dark-pro' | string",
          "required": false,
          "default": "'github-dark'",
          "description": "Shiki theme for code highlighting"
        }
      ],
      "slots": [
        {
          "name": "header",
          "description": "Custom header slot that receives frontmatter data",
          "props": [
            "frontmatter"
          ]
        },
        {
          "name": "error",
          "description": "Custom error display slot",
          "props": [
            "error"
          ]
        }
      ],
      "features": [
        "YAML frontmatter extraction",
        "Async markdown parsing with Shiki highlighting",
        "Loading and error state management",
        "Support for admonitions, footnotes, definition lists, mermaid diagrams"
      ],
      "example": "<MarkdownPage content={markdownString} theme=\"github-dark\">\n  {#snippet header(frontmatter)}\n    <h1>{frontmatter.title}</h1>\n  {/snippet}\n</MarkdownPage>"
    },
    {
      "name": "CodeBlock",
      "description": "Standalone syntax-highlighted code block using Shiki",
      "category": "core",
      "props": [
        {
          "name": "code",
          "type": "string",
          "required": true,
          "description": "The code to highlight"
        },
        {
          "name": "language",
          "type": "string",
          "required": false,
          "default": "'text'",
          "description": "Programming language for highlighting"
        },
        {
          "name": "theme",
          "type": "string",
          "required": false,
          "default": "'github-dark'",
          "description": "Shiki theme"
        },
        {
          "name": "filename",
          "type": "string",
          "required": false,
          "description": "Optional filename to display above code"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Custom class"
        }
      ],
      "features": [
        "Shiki syntax highlighting",
        "Optional filename header",
        "Automatic language detection fallback",
        "Pre-loaded common languages"
      ],
      "example": "<CodeBlock\n  code=\"const x = 1;\"\n  language=\"typescript\"\n  filename=\"example.ts\"\n/>"
    },
    {
      "name": "DocsHub",
      "description": "Documentation index/navigation component with card-based grid layout and optional search",
      "category": "navigation",
      "props": [
        {
          "name": "config",
          "type": "DocsHubConfig",
          "required": true,
          "description": "Configuration with sections and search options"
        },
        {
          "name": "columns",
          "type": "1 | 2 | 3 | 4",
          "required": false,
          "default": "3",
          "description": "Number of grid columns"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Custom class"
        }
      ],
      "slots": [
        {
          "name": "header",
          "description": "Custom header slot"
        },
        {
          "name": "card",
          "description": "Custom card renderer",
          "props": [
            "item"
          ]
        }
      ],
      "features": [
        "Responsive grid layout",
        "Section grouping with collapsible headers",
        "Optional search filtering",
        "Custom card rendering support"
      ],
      "example": "<DocsHub config={{\n  sections: [\n    { id: 'getting-started', title: 'Getting Started', items: [...] }\n  ],\n  search: { enabled: true, placeholder: 'Search docs...' }\n}} columns={3} />"
    },
    {
      "name": "DocsCard",
      "description": "Individual documentation item card with icon, badge, and description support",
      "category": "navigation",
      "props": [
        {
          "name": "item",
          "type": "DocsItem",
          "required": true,
          "description": "Documentation item data"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Custom class"
        }
      ],
      "features": [
        "Icon support",
        "Badge display (new/updated/deprecated)",
        "Tag display",
        "External link handling"
      ],
      "example": "<DocsCard item={{\n  title: 'Introduction',\n  description: 'Getting started guide',\n  href: '/docs/intro',\n  icon: '📚',\n  badge: 'new'\n}} />"
    },
    {
      "name": "TableOfContents",
      "description": "Auto-generated navigation from headings with scroll tracking via IntersectionObserver",
      "category": "navigation",
      "props": [
        {
          "name": "html",
          "type": "string",
          "required": true,
          "description": "HTML content to extract headings from"
        },
        {
          "name": "maxDepth",
          "type": "number",
          "required": false,
          "default": "3",
          "description": "Maximum heading level to include (1-6)"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Custom class"
        },
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Optional title above the TOC"
        },
        {
          "name": "sticky",
          "type": "boolean",
          "required": false,
          "description": "Whether to use sticky positioning"
        }
      ],
      "features": [
        "Automatic heading extraction",
        "IntersectionObserver scroll tracking",
        "Smooth scroll to headings",
        "Hierarchical indentation",
        "Active state highlighting"
      ],
      "example": "<TableOfContents html={renderedHtml} maxDepth={3} title=\"On this page\" sticky />"
    },
    {
      "name": "TocPanel",
      "description": "Responsive collapsible table of contents panel with mobile drawer support",
      "category": "navigation",
      "props": [
        {
          "name": "html",
          "type": "string",
          "required": true,
          "description": "HTML content for TOC extraction"
        },
        {
          "name": "maxDepth",
          "type": "number",
          "required": false,
          "default": "3",
          "description": "Maximum heading depth"
        },
        {
          "name": "breakpoint",
          "type": "'lg' | 'xl' | '2xl'",
          "required": false,
          "default": "'lg'",
          "description": "Breakpoint for desktop sidebar display"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Custom class"
        }
      ],
      "features": [
        "Mobile flyout drawer with attached tab",
        "Desktop sidebar display",
        "Keyboard support (Escape to close)",
        "Focus restoration on close",
        "Responsive breakpoint variants"
      ],
      "example": "<TocPanel html={renderedHtml} breakpoint=\"lg\" />"
    },
    {
      "name": "MermaidDiagram",
      "description": "Individual Mermaid diagram renderer with theme support and error handling",
      "category": "content",
      "props": [
        {
          "name": "code",
          "type": "string",
          "required": true,
          "description": "Mermaid diagram code"
        },
        {
          "name": "theme",
          "type": "string",
          "required": false,
          "default": "'default'",
          "description": "Mermaid theme"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Custom class"
        }
      ],
      "features": [
        "Client-side Mermaid rendering",
        "Theme support",
        "Dynamic ID generation",
        "Fallback/error handling"
      ],
      "peerDependency": "mermaid@^11.0.0 (optional)",
      "example": "<MermaidDiagram code=\"graph TD\\n  A --> B\" theme=\"default\" />"
    },
    {
      "name": "MermaidInit",
      "description": "Auto-initialize all Mermaid diagrams on page with optional MutationObserver for dynamic content",
      "category": "content",
      "props": [
        {
          "name": "watch",
          "type": "boolean",
          "required": false,
          "default": "false",
          "description": "Watch for dynamic content with MutationObserver"
        }
      ],
      "features": [
        "Page-wide diagram initialization",
        "Optional MutationObserver for dynamic content",
        "No visual output (utility component)"
      ],
      "peerDependency": "mermaid@^11.0.0 (optional)",
      "example": "<MermaidInit watch={true} />"
    }
  ],
  "utilities": {
    "parser": {
      "description": "Markdown parsing utilities",
      "functions": [
        {
          "name": "parseMarkdown",
          "signature": "(content: string, options?: ParseOptions) => Promise<ParsedMarkdown>",
          "description": "Parse markdown with YAML frontmatter extraction and Shiki syntax highlighting",
          "example": "import { parseMarkdown } from '@classic-homes/theme-docs';\n\nconst result = await parseMarkdown(`---\ntitle: My Doc\n---\n# Hello`);\nconsole.log(result.frontmatter?.title); // 'My Doc'\nconsole.log(result.html); // rendered HTML"
        },
        {
          "name": "extractToc",
          "signature": "(html: string, maxDepth?: number) => TocEntry[]",
          "description": "Extract table of contents entries from rendered HTML",
          "example": "import { extractToc } from '@classic-homes/theme-docs';\n\nconst toc = extractToc(html, 3);\n// [{ text: 'Introduction', level: 1, id: 'introduction' }, ...]"
        }
      ]
    },
    "highlighter": {
      "description": "Syntax highlighting utilities using Shiki",
      "functions": [
        {
          "name": "highlightCode",
          "signature": "(code: string, language: string, options?: { theme?: string }) => Promise<string>",
          "description": "Highlight code with automatic language/theme loading",
          "example": "import { highlightCode } from '@classic-homes/theme-docs';\n\nconst html = await highlightCode('const x = 1;', 'typescript', { theme: 'github-dark' });"
        },
        {
          "name": "getHighlighter",
          "signature": "() => Promise<Highlighter>",
          "description": "Get or create singleton Shiki highlighter instance"
        },
        {
          "name": "escapeHtml",
          "signature": "(str: string) => string",
          "description": "Escape HTML entities in a string"
        }
      ],
      "preloadedLanguages": [
        "javascript",
        "typescript",
        "svelte",
        "html",
        "css",
        "json",
        "bash",
        "markdown",
        "yaml",
        "python",
        "sql"
      ],
      "preloadedThemes": [
        "github-dark",
        "github-light",
        "one-dark-pro"
      ]
    },
    "extensions": {
      "description": "Marked markdown extension functions",
      "functions": [
        {
          "name": "admonitionExtension",
          "signature": "() => MarkedExtension",
          "description": "Extension for callouts/admonitions (:::note, :::tip, :::warning, :::important, :::caution)"
        },
        {
          "name": "footnoteExtension",
          "signature": "() => MarkedExtension",
          "description": "Extension for footnotes ([^1] references and [^1]: definitions)"
        },
        {
          "name": "definitionListExtension",
          "signature": "() => MarkedExtension",
          "description": "Extension for definition lists (Term\\n: Definition)"
        },
        {
          "name": "mermaidExtension",
          "signature": "() => MarkedExtension",
          "description": "Extension for Mermaid diagram placeholders"
        },
        {
          "name": "getAllExtensions",
          "signature": "() => MarkedExtension[]",
          "description": "Get all extensions as an array"
        },
        {
          "name": "resetFootnoteStore",
          "signature": "() => void",
          "description": "Reset footnote state between documents"
        },
        {
          "name": "renderFootnotes",
          "signature": "() => string",
          "description": "Render collected footnotes as HTML section"
        }
      ],
      "constants": [
        {
          "name": "ADMONITION_TYPES",
          "type": "readonly ['note', 'tip', 'warning', 'important', 'caution']",
          "description": "Array of supported admonition types"
        }
      ]
    },
    "helpers": {
      "description": "Helper utilities",
      "functions": [
        {
          "name": "cn",
          "signature": "(...inputs: ClassValue[]) => string",
          "description": "Merge Tailwind classes (clsx + tailwind-merge)"
        }
      ],
      "reexports": [
        {
          "name": "tv",
          "from": "tailwind-variants",
          "description": "Tailwind variants function"
        },
        {
          "name": "VariantProps",
          "from": "tailwind-variants",
          "description": "Type helper for variant props"
        }
      ]
    }
  },
  "types": [
    {
      "name": "ParsedMarkdown",
      "description": "Result from parsing markdown",
      "properties": [
        {
          "name": "frontmatter",
          "type": "FrontmatterData | null",
          "required": true,
          "description": "Extracted frontmatter data"
        },
        {
          "name": "markdown",
          "type": "string",
          "required": true,
          "description": "Raw markdown without frontmatter"
        },
        {
          "name": "html",
          "type": "string",
          "required": true,
          "description": "Rendered HTML content"
        }
      ]
    },
    {
      "name": "ParseOptions",
      "description": "Options for parseMarkdown",
      "properties": [
        {
          "name": "theme",
          "type": "string",
          "required": false,
          "default": "'github-dark'",
          "description": "Shiki theme for code highlighting"
        },
        {
          "name": "generateHeadingIds",
          "type": "boolean",
          "required": false,
          "default": "true",
          "description": "Whether to generate heading IDs"
        }
      ]
    },
    {
      "name": "FrontmatterData",
      "description": "Standard frontmatter fields",
      "properties": [
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Document title"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Document description/summary"
        },
        {
          "name": "tags",
          "type": "string[]",
          "required": false,
          "description": "Tags for categorization"
        },
        {
          "name": "date",
          "type": "string | Date",
          "required": false,
          "description": "Publication/last updated date"
        },
        {
          "name": "author",
          "type": "string | AuthorInfo",
          "required": false,
          "description": "Author information"
        },
        {
          "name": "category",
          "type": "string",
          "required": false,
          "description": "Document category"
        },
        {
          "name": "order",
          "type": "number",
          "required": false,
          "description": "Sort order within category"
        },
        {
          "name": "draft",
          "type": "boolean",
          "required": false,
          "description": "Whether document is draft"
        },
        {
          "name": "icon",
          "type": "string",
          "required": false,
          "description": "Custom icon (emoji or icon name)"
        },
        {
          "name": "readingTime",
          "type": "string",
          "required": false,
          "description": "Estimated reading time"
        },
        {
          "name": "related",
          "type": "string[]",
          "required": false,
          "description": "Related document slugs"
        },
        {
          "name": "[key: string]",
          "type": "unknown",
          "required": false,
          "description": "Custom metadata fields"
        }
      ]
    },
    {
      "name": "AuthorInfo",
      "description": "Detailed author information",
      "properties": [
        {
          "name": "name",
          "type": "string",
          "required": true,
          "description": "Author's display name"
        },
        {
          "name": "email",
          "type": "string",
          "required": false,
          "description": "Author's email address"
        },
        {
          "name": "avatar",
          "type": "string",
          "required": false,
          "description": "URL to author's avatar image"
        }
      ]
    },
    {
      "name": "DocsItem",
      "description": "Individual documentation item",
      "properties": [
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Display title"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Brief description"
        },
        {
          "name": "href",
          "type": "string",
          "required": true,
          "description": "Link/route to the document"
        },
        {
          "name": "icon",
          "type": "string",
          "required": false,
          "description": "Icon identifier or emoji"
        },
        {
          "name": "tags",
          "type": "string[]",
          "required": false,
          "description": "Tags for filtering"
        },
        {
          "name": "badge",
          "type": "'new' | 'updated' | 'deprecated' | string",
          "required": false,
          "description": "Badge to display"
        },
        {
          "name": "external",
          "type": "boolean",
          "required": false,
          "description": "Whether link opens in new tab"
        }
      ]
    },
    {
      "name": "DocsSection",
      "description": "Documentation section/category",
      "properties": [
        {
          "name": "id",
          "type": "string",
          "required": true,
          "description": "Section identifier"
        },
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Section title"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Section description"
        },
        {
          "name": "items",
          "type": "DocsItem[]",
          "required": true,
          "description": "Items in this section"
        },
        {
          "name": "collapsed",
          "type": "boolean",
          "required": false,
          "description": "Collapse section by default"
        }
      ]
    },
    {
      "name": "DocsHubConfig",
      "description": "Full DocsHub configuration",
      "properties": [
        {
          "name": "sections",
          "type": "DocsSection[]",
          "required": true,
          "description": "Documentation sections"
        },
        {
          "name": "search",
          "type": "{ enabled: boolean; placeholder?: string }",
          "required": false,
          "description": "Search configuration"
        }
      ]
    },
    {
      "name": "TocEntry",
      "description": "Table of contents heading entry",
      "properties": [
        {
          "name": "text",
          "type": "string",
          "required": true,
          "description": "Heading text"
        },
        {
          "name": "level",
          "type": "number",
          "required": true,
          "description": "Heading level (1-6)"
        },
        {
          "name": "id",
          "type": "string",
          "required": true,
          "description": "Generated ID for linking"
        }
      ]
    },
    {
      "name": "AdmonitionType",
      "description": "Supported admonition types",
      "type": "'note' | 'tip' | 'warning' | 'important' | 'caution'"
    }
  ],
  "markdownExtensions": {
    "admonitions": {
      "description": "Callout boxes for important information",
      "types": [
        "note",
        "tip",
        "warning",
        "important",
        "caution"
      ],
      "syntax": ":::type Title (optional)\\nContent\\n:::",
      "example": ":::warning Security Notice\\nDo not share your API keys.\\n:::"
    },
    "footnotes": {
      "description": "Reference-style footnotes",
      "syntax": "Text with footnote[^1]\\n\\n[^1]: Footnote content.",
      "example": "According to research[^1], this is true.\\n\\n[^1]: Smith et al., 2024"
    },
    "definitionLists": {
      "description": "Term/definition pairs",
      "syntax": "Term\\n: Definition",
      "example": "API\\n: Application Programming Interface"
    },
    "mermaidDiagrams": {
      "description": "Diagrams rendered client-side by Mermaid",
      "syntax": "```mermaid\\ngraph TD\\n  A --> B\\n```",
      "note": "Requires mermaid@^11.0.0 peer dependency for rendering"
    }
  },
  "styles": {
    "path": "@classic-homes/theme-docs/styles",
    "description": "Comprehensive markdown content styling with CSS custom properties support",
    "layers": [
      "docs.base",
      "docs.components",
      "docs.utilities"
    ],
    "features": [
      "Heading styles (h1-h6) with responsive sizing",
      "Paragraph and link styles",
      "List styles (ordered, unordered, nested)",
      "Code block styling with Shiki integration",
      "Blockquote styling",
      "Table styling with alternating rows",
      "Admonition styling (5 types with color-coded borders/icons)",
      "Footnote section styling with backlinks",
      "Definition list styling",
      "Mermaid diagram container styling"
    ]
  },
  "peerDependencies": {
    "mermaid": "^11.0.0",
    "svelte": "^5.0.0"
  },
  "dependencies": {
    "@classic-homes/theme-tokens": "*",
    "clsx": "^2.1.0",
    "js-yaml": "^4.1.0",
    "marked": "^17.0.0",
    "shiki": "^1.0.0",
    "tailwind-merge": "^2.2.0",
    "tailwind-variants": "^0.3.0"
  }
}